/**
 * ttAltarRules — Twisted Treeline West/East altar capture & team buffs (Season 2).
 * Pure domain: no DOM, no Application imports.
 */

export const ALTAR_BUFF_ID = 'tt_altar_buff';

export const TT_ALTAR_CONFIG = {
  unlockTime: 180,
  captureTime: 3.0,
  captureRadius: 1.5,
  oneAltar: { msPercent: 0.10 },
  twoAltars: { msPercent: 0.10, adPercent: 0.10, apPercent: 0.10 }
};

export function isAltarUnlocked(gameTime = 0) {
  return gameTime >= TT_ALTAR_CONFIG.unlockTime;
}

export function getAltarPosition(altar) {
  if (!altar) return { x: 0, y: 0 };
  const x = altar.x ?? 0;
  const y = altar.y !== undefined ? altar.y : (altar.z ?? 0);
  return { x, y };
}

export function isChampionOnAltar(entity, altar, radius = TT_ALTAR_CONFIG.captureRadius) {
  if (!entity || !altar) return false;
  const pos = getAltarPosition(altar);
  const entityZ = entity.z !== undefined ? entity.z : entity.y;
  return Math.hypot(entity.x - pos.x, entityZ - pos.y) <= radius;
}

export function tickAltarCapture(altar, teamId, dt, ctx = {}) {
  const gameTime = ctx.gameTime ?? 0;
  const onAltar = ctx.onAltar === true;
  const captureTime = altar.captureTime || TT_ALTAR_CONFIG.captureTime;
  const owner = altar.teamOwner || altar.capturedBy || null;

  if (!isAltarUnlocked(gameTime)) {
    return freezeAltar(altar, owner);
  }

  if (!onAltar || !teamId) {
    return resetCaptureProgress(altar, owner);
  }

  return advanceCapture(altar, teamId, dt, captureTime, owner);
}

function freezeAltar(altar, owner) {
  return {
    ...altar,
    progress: 0,
    captureProgress: 0,
    teamOwner: owner,
    capturedBy: owner
  };
}

function resetCaptureProgress(altar, owner) {
  return {
    ...altar,
    progress: 0,
    captureProgress: owner ? 1 : 0,
    teamOwner: owner,
    capturedBy: owner
  };
}

function advanceCapture(altar, teamId, dt, captureTime, owner) {
  if (owner === teamId) {
    return {
      ...altar,
      progress: captureTime,
      captureProgress: 1,
      teamOwner: owner,
      capturedBy: owner
    };
  }

  const current = altar.progress || 0;
  const nextProgress = current + dt;
  const captured = nextProgress >= captureTime;
  const newOwner = captured ? teamId : owner;

  return {
    ...altar,
    progress: Math.min(captureTime, nextProgress),
    captureProgress: Math.min(1, nextProgress / captureTime),
    teamOwner: newOwner,
    capturedBy: newOwner
  };
}

export function countOwnedAltars(altars, team) {
  if (!Array.isArray(altars) || !team) return 0;
  return altars.filter(a => (a.teamOwner || a.capturedBy) === team).length;
}

export function getAltarBuffSpec(ownedCount) {
  if (ownedCount >= 2) {
    return { id: ALTAR_BUFF_ID, name: 'Twin Relics', ...TT_ALTAR_CONFIG.twoAltars };
  }
  if (ownedCount >= 1) {
    return { id: ALTAR_BUFF_ID, name: 'Altar Blessed', ...TT_ALTAR_CONFIG.oneAltar };
  }
  return null;
}

export function syncAltarTeamBuffs(champions, altars) {
  if (!Array.isArray(champions)) return;

  champions.forEach(champion => {
    if (!champion || champion.type !== 'champion') return;
    if (!champion.buffs) champion.buffs = [];

    removeAltarBuff(champion);
    const owned = countOwnedAltars(altars, champion.team);
    const spec = getAltarBuffSpec(owned);
    if (spec) champion.buffs.push({ ...spec });
  });
}

function removeAltarBuff(champion) {
  const idx = champion.buffs.findIndex(b => b.id === ALTAR_BUFF_ID);
  if (idx !== -1) champion.buffs.splice(idx, 1);
}
